import { useAuth } from '@/lib/auth/AuthProvider';
import { NavLink } from 'react-router-dom';
import { 
  LogOut, 
  Moon, 
  Sun, 
  User,
  Menu,
  X,
  LayoutDashboard,
  Tag,
  Hash,
  BookOpen,
  Calendar,
  Settings2,
  Users,
  MessagesSquare,
  FolderOpen,
  FileText,
  MessageSquare,
  Zap,
  UserCircle,
} from 'lucide-react';
import { useState, useEffect } from 'react';

type NavItem = {
  name: string;
  href: string;
  icon: React.ElementType;
  color?: 'purple' | 'rose' | 'coral' | 'teal';
};

type NavSection = {
  title: string;
  items: NavItem[];
};

const mobileNavItems: (NavItem | NavSection)[] = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, color: 'purple' },
  {
    title: 'Configuration',
    items: [
      { name: 'Keywords', href: '/keywords', icon: Tag, color: 'purple' },
      { name: 'Projects', href: '/projects', icon: Hash, color: 'rose' },
      { name: 'Instructions', href: '/instructions', icon: BookOpen, color: 'coral' },
      { name: 'Schedule', href: '/schedule', icon: Calendar, color: 'teal' },
      { name: 'Preferences', href: '/preferences', icon: Settings2, color: 'purple' },
    ],
  },
  {
    title: 'Sources',
    items: [
      { name: 'People', href: '/people', icon: Users, color: 'rose' },
      { name: 'Conversations', href: '/conversations', icon: MessagesSquare, color: 'coral' },
      { name: 'Locations', href: '/locations', icon: FolderOpen, color: 'teal' },
    ],
  },
  { name: 'Outputs', href: '/outputs', icon: FileText, color: 'rose' },
  { name: 'History', href: '/history', icon: MessageSquare, color: 'coral' },
  {
    title: 'Account',
    items: [
      { name: 'Settings', href: '/settings', icon: UserCircle, color: 'rose' },
    ],
  },
];

const colorClasses = {
  purple: 'text-primary-500',
  rose: 'text-rose-500',
  coral: 'text-coral-500',
  teal: 'text-teal-500',
};

function isSection(item: NavItem | NavSection): item is NavSection {
  return 'title' in item;
}

export function Header() {
  const { user, logout, isInTeams } = useAuth();
  const [isDark, setIsDark] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Check initial theme - in Teams, this is controlled by Teams SDK
    const isDarkMode = document.documentElement.classList.contains('dark');
    setIsDark(isDarkMode);
  }, []);

  const toggleTheme = () => {
    // Don't allow manual theme toggle in Teams - it follows Teams theme
    if (isInTeams) return;
    
    const newDark = !isDark;
    setIsDark(newDark);
    document.documentElement.classList.toggle('dark', newDark);
    localStorage.setItem('theme', newDark ? 'dark' : 'light');
  };

  const renderNavItem = (item: NavItem) => {
    const colorClass = colorClasses[item.color || 'purple'];
    
    return (
      <NavLink
        key={item.name}
        to={item.href}
        onClick={() => setIsMobileMenuOpen(false)}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all duration-300 ${
            isActive
              ? 'bg-primary-500/10 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400'
              : 'text-dark-500 dark:text-dark-400 hover:bg-dark-100 dark:hover:bg-dark-800'
          }`
        }
      >
        {({ isActive }) => (
          <>
            <item.icon className={`h-5 w-5 ${isActive ? 'text-primary-400' : colorClass}`} />
            <span>{item.name}</span>
          </>
        )}
      </NavLink>
    );
  };

  return (
    <>
      <header className="flex h-16 items-center justify-between border-b border-dark-100 dark:border-dark-800 bg-white/80 dark:bg-dark-900/80 backdrop-blur-xl px-4 lg:px-6">
        {/* Mobile menu button */}
        <button 
          className="lg:hidden p-2.5 rounded-xl bg-dark-100 dark:bg-dark-800 hover:bg-dark-200 dark:hover:bg-dark-700 transition-colors"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? (
            <X className="h-5 w-5 text-dark-600 dark:text-dark-300" />
          ) : (
            <Menu className="h-5 w-5 text-dark-600 dark:text-dark-300" />
          )}
        </button>

        {/* Teams indicator */}
        {isInTeams && (
          <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-500/10 dark:bg-teal-500/20">
            <span className="inline-flex h-2 w-2 rounded-full bg-teal-500 animate-pulse" />
            <span className="text-xs font-medium text-teal-600 dark:text-teal-400">Running in Teams</span>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right side actions */}
        <div className="flex items-center gap-2">
          {/* Theme toggle */}
          {!isInTeams && (
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-xl bg-dark-100 dark:bg-dark-800 hover:bg-dark-200 dark:hover:bg-dark-700 transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <Sun className="h-5 w-5 text-coral-500" />
              ) : (
                <Moon className="h-5 w-5 text-primary-500" />
              )}
            </button>
          )}

          {/* User menu */}
          <div className="flex items-center gap-3 border-l border-dark-200 dark:border-dark-700 pl-4 ml-2">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-semibold text-dark-900 dark:text-white">
                {user?.name || 'User'}
              </p>
              {!isInTeams && (
                <p className="text-xs text-dark-500">
                  {user?.username}
                </p>
              )}
            </div>
            
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 text-white shadow-lg shadow-primary-500/20">
              {user?.name ? (
                <span className="text-sm font-bold">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              ) : (
                <User className="h-5 w-5" />
              )}
            </div>

            {/* Sign out */}
            {!isInTeams && (
              <button
                onClick={() => logout()}
                className="p-2.5 rounded-xl hover:bg-rose-500/10 text-dark-500 hover:text-rose-500 transition-all duration-300"
                aria-label="Sign out"
              >
                <LogOut className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile navigation drawer */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-dark-950/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Drawer */}
          <div className="fixed inset-y-0 left-0 w-72 bg-white dark:bg-dark-900 border-r border-dark-100 dark:border-dark-800 z-50 lg:hidden overflow-y-auto animate-slide-in-right">
            <div className="flex h-16 items-center gap-3 border-b border-dark-100 dark:border-dark-800 px-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 shadow-lg shadow-primary-500/20">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <h1 className="font-display text-lg font-bold text-dark-900 dark:text-white">
                Impact Agent
              </h1>
            </div>
            
            <nav className="p-4 space-y-1">
              {mobileNavItems.map((item, index) => {
                if (isSection(item)) {
                  return (
                    <div key={item.title} className={index > 0 ? 'pt-4' : ''}>
                      <div className="px-4 mb-2 text-xs font-bold uppercase tracking-wider text-dark-400 dark:text-dark-500">
                        {item.title}
                      </div>
                      <div className="space-y-1">
                        {item.items.map((subItem) => renderNavItem(subItem))}
                      </div>
                    </div>
                  );
                }
                return renderNavItem(item);
              })}
            </nav>
          </div>
        </>
      )}
    </>
  );
}
